import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Car as CarIcon, RefreshCw, User, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatPrice, formatKm } from "@/lib/format";
import api from "@/lib/api";
import { proxied } from "@/lib/imageProxy";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("hu-HU", {
      year: "numeric", month: "long", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export function AdminPendingCars() {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/pending-cars");
      setCars(r.data.cars || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Betöltés sikertelen");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (car) => {
    setBusyId(car.id);
    try {
      await api.post(`/admin/cars/${car.id}/approve`);
      toast.success(`${car.make} ${car.model} közzétéve`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Jóváhagyás sikertelen");
    } finally { setBusyId(null); }
  };

  const reject = async (car) => {
    if (!window.confirm(`Elutasítod "${car.make} ${car.model}" közzétételét? A hirdetés visszakerül az eladóhoz.`)) return;
    setBusyId(car.id);
    try {
      await api.post(`/admin/cars/${car.id}/reject`);
      toast.success("Elutasítva");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Elutasítás sikertelen");
    } finally { setBusyId(null); }
  };

  return (
    <div data-testid="admin-pending-cars-panel" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-slate-400">
          <span data-testid="admin-pending-count" className="font-heading text-2xl font-light text-white">{cars.length}</span> jóváhagyásra váró hirdetés
        </div>
        <button
          data-testid="admin-pending-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass hover:bg-white/10 text-xs text-slate-300"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Frissítés
        </button>
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-electric animate-spin" /></div>
      ) : cars.length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center text-slate-400 text-sm" data-testid="admin-pending-empty">
          Nincs jóváhagyásra váró hirdetés.
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-pending-list">
          {cars.map((car) => (
            <li
              key={car.id}
              data-testid={`admin-pending-item-${car.id}`}
              className="glass rounded-2xl p-4 md:p-5 flex flex-col md:flex-row gap-4 md:items-center"
            >
              <img
                src={proxied(car.image)}
                alt={`${car.make} ${car.model}`}
                referrerPolicy="no-referrer"
                className="w-full md:w-40 h-32 md:h-24 object-cover rounded-xl"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <CarIcon className="w-4 h-4 text-electric" strokeWidth={1.75} />
                  <div className="font-heading text-lg font-light">
                    <span data-testid="admin-pending-make-model">{car.make} {car.model}</span>
                    <span className="text-slate-400 text-sm ml-2">· {car.year}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 text-[10px] uppercase tracking-wider">
                    Jóváhagyásra vár
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400 flex-wrap">
                  <span className="text-electric font-medium">{formatPrice(car.price)}</span>
                  <span>·</span>
                  <span>{formatKm(car.mileage)}</span>
                  <span>·</span>
                  <span>{car.fuel} · {car.transmission}</span>
                </div>
                {car.owner && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                    <User className="w-3.5 h-3.5" />
                    <span data-testid="admin-pending-owner">
                      {car.owner.name || car.owner.email}
                      {car.owner.name && car.owner.email ? ` (${car.owner.email})` : ""}
                    </span>
                  </div>
                )}
                <div className="mt-1 text-[11px] text-slate-500">
                  Feltöltve: {formatDate(car.created_at)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={car.slug ? `/auto/${car.slug}` : `/auto/${car.id}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`admin-pending-preview-${car.id}`}
                  title="Előnézet új fülön"
                  className="w-9 h-9 rounded-full glass hover:bg-white/10 flex items-center justify-center text-slate-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" strokeWidth={1.75} />
                </a>
                <button
                  data-testid={`admin-pending-reject-${car.id}`}
                  disabled={busyId === car.id}
                  onClick={() => reject(car)}
                  title="Elutasítás"
                  className="w-9 h-9 rounded-full glass hover:bg-red-500/15 hover:text-red-400 flex items-center justify-center text-slate-300 transition-colors"
                >
                  <XCircle className="w-4 h-4" strokeWidth={1.75} />
                </button>
                <button
                  data-testid={`admin-pending-approve-${car.id}`}
                  disabled={busyId === car.id}
                  onClick={() => approve(car)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-white text-sm font-medium transition-all"
                >
                  {busyId === car.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4" strokeWidth={1.75} />
                  )}
                  Jóváhagyás
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
